import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HTMLFlipBook from 'react-pageflip';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Printer, Download, Maximize2, ArrowLeft, Loader2 } from 'lucide-react';
import flipAudio from '../Components/Images/AUDIO-2025-02-07-17-56-20.mp3';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const Pages = React.forwardRef((props, ref) => (
  <div className="demoPage" ref={ref} style={{ backgroundColor: '#ffffff', boxShadow: '0 10px 25px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
    {props.children}
  </div>
));
Pages.displayName = 'Pages';

export default function PDFViewer() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const file = queryParams.get('file');

  const flipSound = new Audio(flipAudio);
  const flipbookRef = useRef(null);
  const containerRef = useRef(null);

  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoomScale, setZoomScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 480, height: 680 });

  useEffect(() => {
    function updateDimensions() {
      const screenWidth = window.innerWidth;
      if (screenWidth > 1024) {
        setDimensions({ width: 480, height: 680 });
      } else if (screenWidth > 768) {
        setDimensions({ width: 380, height: 540 });
      } else {
        setDimensions({ width: 300, height: 420 });
      }
    }
    window.addEventListener('resize', updateDimensions);
    updateDimensions();
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Keyboard Navigation & Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        flipToNextPage();
      } else if (e.key === 'ArrowLeft') {
        flipToPrevPage();
      } else if (e.key === 'Escape') {
        setIsFullscreen(false);
      } else if (e.key === 'p' || e.key === 'P') {
        handlePrint();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, numPages]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleFlip = (event) => {
    flipSound.play().catch((e) => console.error("Audio error:", e));
    setCurrentPage(event.data + 1);
  };

  const flipToNextPage = () => {
    if (currentPage < numPages && flipbookRef.current) {
      flipbookRef.current.pageFlip().flipNext(['top']);
    }
  };

  const flipToPrevPage = () => {
    if (currentPage > 1 && flipbookRef.current) {
      flipbookRef.current.pageFlip().flipPrev(['top']);
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(err => console.error(err));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  const handlePrint = () => {
    const printWindow = window.open(file, '_blank');
    if (printWindow) {
      printWindow.focus();
      printWindow.print();
    }
  };

  return (
    <div 
      ref={containerRef}
      style={{ 
        minHeight: '100vh', 
        backgroundColor: '#0f172a', 
        color: '#f8fafc', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        paddingTop: isFullscreen ? '0' : '80px',
        paddingBottom: '80px',
        position: 'relative',
        fontFamily: 'sans-serif'
      }}
    >
      {/* Back button */}
      {!isFullscreen && (
        <button 
          onClick={() => {
            if (window.history.state && window.history.state.idx > 0) {
              navigate(-1);
            } else {
              navigate('/Academics');
            }
          }}
          style={{ position: 'absolute', top: '100px', left: '24px', display: 'flex', alignItems: 'center', gap: '8px', border: 'none', background: 'rgba(255,255,255,0.08)', color: '#ffffff', padding: '8px 16px', borderRadius: 'var(--border-radius-md)', cursor: 'pointer', zIndex: 10 }}
        >
          <ArrowLeft size={16} /> Back
        </button>
      )}

      {/* Reader Main Space */}
      <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', margin: '40px 0', transform: `scale(${zoomScale})`, transition: 'transform 0.2s ease' }}>
        {file ? (
          <Document 
            file={file} 
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                <Loader2 size={36} className="animate-spin" style={{ color: 'var(--accent-color)', animation: 'spin 1s linear infinite' }} />
                <span>Loading Document Pages...</span>
              </div>
            }
          >
            {numPages && (
              <HTMLFlipBook
                ref={flipbookRef}
                width={dimensions.width}
                height={dimensions.height}
                showCover={false}
                mobileScrollSupport={true}
                onFlip={handleFlip}
                style={{ margin: '0 auto' }}
              >
                {[...Array(numPages).keys()].map((pNum) => (
                  <Pages key={pNum} number={pNum + 1}>
                    <Page
                      pageNumber={pNum + 1}
                      width={dimensions.width}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                    />
                  </Pages>
                ))}
              </HTMLFlipBook>
            )}
          </Document>
        ) : (
          <h3 style={{ color: 'var(--text-muted)' }}>No Document Selected</h3>
        )}
      </div>

      {/* Modern Control Toolbar */}
      {numPages && (
        <div style={toolbarWrapperStyle}>
          <div style={toolbarStyle}>
            {/* Prev */}
            <button onClick={flipToPrevPage} disabled={currentPage === 1} style={toolbarButtonStyle}>
              <ChevronLeft size={20} />
            </button>

            {/* Page info */}
            <span style={{ fontSize: '0.9rem', fontWeight: 600, minWidth: '80px', textAlign: 'center' }}>
              {currentPage} / {numPages}
            </span>

            {/* Next */}
            <button onClick={flipToNextPage} disabled={currentPage === numPages} style={toolbarButtonStyle}>
              <ChevronRight size={20} />
            </button>

            <div style={{ width: '1px', height: '24px', backgroundColor: 'rgba(255,255,255,0.15)', margin: '0 8px' }} />

            {/* Zoom controls */}
            <button onClick={() => setZoomScale(z => Math.max(z - 0.15, 0.6))} style={toolbarButtonStyle} title="Zoom Out">
              <ZoomOut size={18} />
            </button>
            <button onClick={() => setZoomScale(z => Math.min(z + 0.15, 2.0))} style={toolbarButtonStyle} title="Zoom In">
              <ZoomIn size={18} />
            </button>
            <button onClick={() => setZoomScale(1.0)} style={toolbarButtonStyle} title="Fit Width">
              <span style={{ fontSize: '0.72rem', fontWeight: 800 }}>FIT</span>
            </button>

            <div style={{ width: '1px', height: '24px', backgroundColor: 'rgba(255,255,255,0.15)', margin: '0 8px' }} />

            {/* Actions */}
            <button onClick={handlePrint} style={toolbarButtonStyle} title="Print Document">
              <Printer size={18} />
            </button>
            
            <a href={file} download style={{ ...toolbarButtonStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', color: 'inherit' }} title="Download PDF">
              <Download size={18} />
            </a>

            <button onClick={toggleFullscreen} style={toolbarButtonStyle} title="Toggle Fullscreen (F)">
              <Maximize2 size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Inline Styles
const toolbarWrapperStyle = {
  position: 'fixed',
  bottom: '24px',
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'center',
  zIndex: 100
};

const toolbarStyle = {
  backgroundColor: 'rgba(30, 41, 59, 0.9)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '9999px',
  padding: '8px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)'
};

const toolbarButtonStyle = {
  background: 'transparent',
  border: 'none',
  color: '#cbd5e1',
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
  outline: 'none'
};

// Add standard spin keyframes & hover styles
const pdfStyles = document.createElement('style');
pdfStyles.innerHTML = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  button[style*="background: transparent"]:hover {
    background-color: rgba(255,255,255,0.1) !important;
    color: #ffffff !important;
  }
  button[style*="background: transparent"]:disabled {
    opacity: 0.3 !important;
    cursor: not-allowed !important;
  }
`;
document.head.appendChild(pdfStyles);
